import React, { useCallback, useEffect } from 'react';
import { Toast } from '@neshane/blueberry';
import { AUTO_EXPIRE_MS, NotificationType } from './notificationModel';

/**
 * Single notification rendered inside the stack.
 *
 * Ported from the legacy `Notification.tsx`: a blueberry `Toast` with
 * variant `success`/`alert`, optional action (removes the toast and calls
 * `action.onClick`), optional description as children, and an auto-expire
 * timer of 5000ms when `canExpire` is true.
 *
 * @param {Object} props
 * @param {import('./notification').Notification} props.notification
 * @param {(notificationId: number) => void} props.removeNotification
 */
export const Notification = ({ notification, removeNotification }) => {
    const handleClose = useCallback(() => {
        if (notification.onClose) {
            notification.onClose();
        }
        removeNotification(notification.id);
    }, [notification, removeNotification]);

    const canExpire = notification.canExpire;

    useEffect(() => {
        let timeoutId;
        if (canExpire) {
            timeoutId = setTimeout(() => {
                if (canExpire) {
                    handleClose();
                }
            }, AUTO_EXPIRE_MS);
        }

        return () => {
            clearTimeout(timeoutId);
        };
    }, [canExpire, handleClose]);

    const handleActionClicked = () => {
        if (notification.action) {
            notification.action.onClick(notification);
        }
        removeNotification(notification.id);
    };

    const variant =
        notification.type === NotificationType.Success ? 'success' : 'alert';
    const hasAction = notification.action !== undefined;

    return (
        <Toast
            variant={variant}
            onClose={handleClose}
            title={notification.message}
            action={
                hasAction
                    ? {
                          text: notification.action?.text || ' ',
                          isLoading: notification.action?.isLoading,
                          onClick: handleActionClicked,
                      }
                    : undefined
            }
        >
            {notification.description}
        </Toast>
    );
};

export default Notification;